import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import ThemeMenu from "./ThemeMenu";
import { Joyride, STATUS, ACTIONS } from "react-joyride";
import {
  Bell, Gear, SignOut, CaretLeft, CaretRight, ChatCircle, BellRinging,
  Buildings, CreditCard, TrendUp, ChartLineUp, ChartBar, Receipt, Palette, Globe,
  EnvelopeSimple, Database, Scales, UsersThree, Gauge, HandPalm, Check,
  ArrowsLeftRight, Question, ShieldCheck, Tray, Percent, BookOpen, Storefront, FirstAid, Heartbeat, Pulse, Handshake, PuzzlePiece
} from "@phosphor-icons/react";

// Super Admin Navigation - ONLY Platform Management Items
const SUPER_ADMIN_NAV_SECTIONS = [
  {
    id: "platform",
    label: "SaaS Platform",
    items: [
      { to: "/", label: "Platform Dashboard", icon: ChartBar },
      { to: "/platform/tenants", label: "SaaS Tenants", icon: Buildings },
      { to: "/platform/subscription-plans", label: "Subscription Plans", icon: CreditCard },
      { to: "/platform/trial-conversions", label: "Trial Conversions", icon: TrendUp },
      { to: "/platform/api-usage", label: "API Usage", icon: ChartLineUp },
      { to: "/platform/revenue", label: "Revenue", icon: ChartBar },
      { to: "/platform/billing", label: "Billing", icon: Receipt },
      { to: "/platform/branding", label: "Branding", icon: Palette },
      { to: "/platform/webhooks", label: "Webhooks", icon: Globe },
      { to: "/platform/email-templates", label: "Email Templates", icon: EnvelopeSimple },
      { to: "/platform/data-management", label: "Data Management", icon: Database },
      { to: "/platform/audit-export", label: "Audit Export", icon: Scales },
      { to: "/platform/tenant-comparison", label: "SaaS Tenant Comparison", icon: UsersThree },
    ],
  },
  {
    id: "revenue_growth",
    label: "Revenue & Growth",
    items: [
      { to: "/platform/cohort-analysis", label: "Cohort Analysis", icon: ChartLineUp },
      { to: "/platform/resellers", label: "Resellers", icon: Handshake },
      { to: "/platform/bespoke", label: "Bespoke Modules", icon: PuzzlePiece },
      { to: "/platform/usage-alerts", label: "Usage Alerts", icon: Gauge },
      { to: "/platform/downgrade", label: "Manage Subscription", icon: HandPalm },
      { to: "/platform/fees", label: "Platform Fees", icon: Percent },
      { to: "/platform/marketplace", label: "Marketplace", icon: Storefront },
      { to: "/platform/hs-library", label: "H&S Library", icon: FirstAid },
    ],
  },
  {
    id: "health",
    label: "Health & Monitoring",
    items: [
      { to: "/platform/system-status", label: "System Status", icon: Heartbeat },
      { to: "/platform/scheduler-health", label: "Scheduler Health", icon: Pulse },
      { to: "/platform/email-health", label: "E-mail Health", icon: EnvelopeSimple },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    items: [
      { to: "/platform/settings", label: "Platform Settings", icon: Gear },
      { to: "/platform/admins", label: "Platform Admins", icon: ShieldCheck },
      { to: "/platform/bulk-supplier-invite", label: "Bulk Supplier Invite", icon: EnvelopeSimple },
      { to: "/knowledge-base?role=platform", label: "Knowledge Base", icon: BookOpen },
    ],
  },
];

export default function SuperAdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const prevUnreadRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("fixli_notif_sound") !== "false");
  const [browserNotifEnabled, setBrowserNotifEnabled] = useState(() => localStorage.getItem("fixli_browser_notif") === "true");
  const [runTour, setRunTour] = useState(false);
  const [ownerReplies, setOwnerReplies] = useState({ unread: 0, configured: false });

  // Owner replies badge (billing mailbox) — refreshed with the bell, plus instantly when the inbox marks something read
  const fetchOwnerReplies = useCallback(async () => {
    try {
      const API = (await import("../lib/api")).default;
      const { data } = await API.get("/platform/payment-recovery/inbox/unread-count");
      setOwnerReplies(data);
    } catch {}
  }, []);
  useEffect(() => {
    fetchOwnerReplies();
    const interval = setInterval(fetchOwnerReplies, 60000);
    window.addEventListener("fixli:owner-replies-changed", fetchOwnerReplies);
    return () => { clearInterval(interval); window.removeEventListener("fixli:owner-replies-changed", fetchOwnerReplies); };
  }, [fetchOwnerReplies]);

  // Platform admin onboarding tour steps
  const tourSteps = [
    {
      target: '[data-tour="dashboard"]',
      content: "Welcome to your Platform Admin Dashboard! Here you'll find key metrics about your tenants, revenue, and platform health.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="tenants"]',
      content: "Manage all your SaaS tenants here. View usage, suspend accounts, and monitor tenant health.",
      placement: "right",
    },
    {
      target: '[data-tour="revenue"]',
      content: "Track billing, subscription plans, and revenue metrics. Monitor MRR, ARR, and trial conversions.",
      placement: "right",
    },
    {
      target: '[data-tour="api-usage"]',
      content: "Monitor API usage across all tenants. Set rate limits and track webhook activity.",
      placement: "right",
    },
    {
      target: '[data-tour="platform-settings"]',
      content: "Configure platform-wide settings including subscription defaults, email templates, maintenance mode, and branding.",
      placement: "right",
    },
    {
      target: '[data-tour="switch-view"]',
      content: "Use this button to switch to Tenant View to test tenant-specific features. You can switch back anytime!",
      placement: "bottom",
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
    
    if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
      setRunTour(false);
    }
  };

  const handleSwitchToTenantView = () => {
    localStorage.setItem("fixli_tenant_testing_mode", "true");
    toast.success("Switched to Tenant View");
    window.location.href = "/";
  };

  // Web Audio API notification chime
  const playNotifSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      [523.25, 659.25].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + i * 0.12);
        gain.gain.linearRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.35);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + i * 0.12);
        osc.stop(now + i * 0.12 + 0.4);
      });
    } catch {}
  }, [soundEnabled]);

  const showBrowserNotif = useCallback((title, body) => {
    if (!browserNotifEnabled) return;
    if (Notification.permission !== "granted") return;
    try {
      const n = new Notification(title, {
        body,
        icon: "/fixlr-icon.png",
        badge: "/fixlr-icon.png",
        tag: "fixli-notif",
        renotify: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
      setTimeout(() => n.close(), 6000);
    } catch {}
  }, [browserNotifEnabled]);

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem("fixli_notif_sound", String(next));
      return next;
    });
  };

  const toggleBrowserNotif = async () => {
    if (!browserNotifEnabled) {
      if (Notification.permission === "default") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") { toast.error("Browser notification permission denied"); return; }
      } else if (Notification.permission === "denied") {
        toast.error("Notifications are blocked. Enable them in your browser settings.");
        return;
      }
      setBrowserNotifEnabled(true);
      localStorage.setItem("fixli_browser_notif", "true");
      toast.success("Browser notifications enabled");
    } else {
      setBrowserNotifEnabled(false);
      localStorage.setItem("fixli_browser_notif", "false");
    }
  };

  const fetchUnread = useCallback(async () => {
    try {
      const API = (await import("../lib/api")).default;
      const { data } = await API.get("/notifications/unread-count");
      const newCount = data.count;
      if (prevUnreadRef.current !== null && newCount > prevUnreadRef.current) {
        const diff = newCount - prevUnreadRef.current;
        playNotifSound();
        try {
          const { data: notifList } = await API.get("/notifications");
          const latest = notifList.find(n => !n.read);
          if (latest) {
            showBrowserNotif(latest.title, latest.message);
            toast(latest.title, {
              description: latest.message,
              icon: <BellRinging size={16} className="text-primary" />,
              duration: 5000,
            });
          } else {
            toast(`${diff} new notification${diff > 1 ? "s" : ""}`, {
              icon: <BellRinging size={16} className="text-primary" />,
              duration: 4000,
            });
          }
        } catch {
          toast(`${diff} new notification${diff > 1 ? "s" : ""}`, {
            icon: <BellRinging size={16} className="text-primary" />,
            duration: 4000,
          });
        }
      }
      prevUnreadRef.current = newCount;
      setUnreadCount(newCount);
    } catch {}
  }, [playNotifSound, showBrowserNotif]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const API = (await import("../lib/api")).default;
      const { data } = await API.get("/notifications");
      setNotifications(data);
    } catch {}
    finally { setNotifLoading(false); }
  }, []);

  useEffect(() => {
    if (notifOpen) fetchNotifications();
  }, [notifOpen, fetchNotifications]);

  const handleNotifClick = async (n) => {
    if (!n.read) {
      try {
        const API = (await import("../lib/api")).default;
        await API.put(`/notifications/${n.id}/read`);
      } catch {}
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setNotifOpen(false);
    // Jump straight to what the notification is about (owner reply thread, deploy health card, …)
    if (n.url && n.url.startsWith("/") && !n.url.startsWith("//")) navigate(n.url);
  };

  const handleMarkAllRead = async () => {
    try {
      const API = (await import("../lib/api")).default;
      await API.put("/notifications/read-all");
    } catch {}
    setNotifications(prev => prev.map(x => ({ ...x, read: true })));
    setUnreadCount(0);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="superadmin-layout">
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        callback={handleJoyrideCallback}
        styles={{ options: { primaryColor: "#135DD4", zIndex: 10000 } }}
      />
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col border-r border-border bg-sidebar transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`} data-testid="superadmin-sidebar">
        <div className="flex items-center justify-center px-3 h-[72px] border-b border-border shrink-0">
          {collapsed ? <img src="/fixlr-icon.png" alt="Fixlr" className="h-9 w-9 object-contain" data-testid="sidebar-logo-icon" /> : (<>
          <img src="/fixlr-logo-dark.png" alt="Fixlr" className="h-14 w-auto dark:hidden" data-testid="sidebar-logo" />
          <img src="/fixlr-logo-light.png" alt="Fixlr" className="h-14 w-auto hidden dark:block" data-testid="sidebar-logo-dark" />
          </>)}
        </div>
        <ScrollArea className="flex-1 py-2">
          <nav className="flex flex-col gap-0.5 px-2">
            {SUPER_ADMIN_NAV_SECTIONS.map((section) => (
              <div key={section.id}>
                {section.label && !collapsed && (
                  <div className="flex items-center gap-2 px-3 pt-5 pb-1.5" data-testid={`nav-section-${section.id}`}>
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground/50">{section.label}</span>
                    <div className="flex-1 h-px bg-border/40" />
                  </div>
                )}
                {section.label && collapsed && <div className="h-px bg-border/40 mx-2 my-3" />}
                {section.items.map((item) => {
                  const isActive = item.to === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(item.to.split("?")[0]);
                  const Icon = item.icon;
                  
                  // Add data-tour attributes for tour targets
                  let dataTour = "";
                  if (item.to === "/") dataTour = "dashboard";
                  else if (item.to === "/platform/tenants") dataTour = "tenants";
                  else if (item.to === "/platform/revenue" || item.to === "/platform/billing") dataTour = "revenue";
                  else if (item.to === "/platform/api-usage") dataTour = "api-usage";
                  else if (item.to === "/platform/settings") dataTour = "platform-settings";
                  
                  return (
                    <Link 
                      key={`${section.id}-${item.to}`} 
                      to={item.to} 
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      data-tour={dataTour}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium nav-link-smooth
                        ${isActive ? "bg-primary text-primary-foreground active" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                    >
                      <Icon size={20} weight={isActive ? "fill" : "regular"} />
                      {!collapsed && <span className="flex-1">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>
        </ScrollArea>
        <div className="border-t border-border p-2 shrink-0 space-y-1">
          <ThemeMenu collapsed={collapsed} />
          <Button variant="ghost" size="sm" className="w-full justify-start gap-3 text-muted-foreground" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle">
            {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
            {!collapsed && <span className="text-xs">Collapse</span>}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 border-b border-border bg-topbar/90 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6 shrink-0 z-50" data-testid="top-header">
          <div className="flex items-center gap-2">
            <img src="/fixlr-logo-dark.png" alt="Fixlr" className="h-9 w-auto dark:hidden md:hidden" />
            <img src="/fixlr-logo-light.png" alt="Fixlr" className="h-9 w-auto hidden dark:block md:dark:hidden" />
            <span className="font-bold text-sm font-[Chivo] md:inline">Platform Admin</span>
          </div>
          
          {/* Switch to Tenant View Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSwitchToTenantView}
            className="hidden md:flex items-center gap-2 text-xs h-8"
            data-testid="switch-tenant-view-btn"
            data-tour="switch-view"
            title="Switch to Tenant View to test tenant-specific features"
          >
            <ArrowsLeftRight size={14} />
            <span>Switch to Tenant View</span>
          </Button>
          
          {/* Help Button (Tour Trigger) */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setRunTour(true)}
            className="hidden md:flex items-center gap-2 text-xs h-8"
            data-testid="help-tour-btn"
            title="Start platform tour"
          >
            <Question size={16} weight="bold" />
            <span>Help</span>
          </Button>
          
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Owner replies (billing mailbox) — only shown once the mailbox is connected or something is waiting */}
            {(ownerReplies.configured || ownerReplies.unread > 0) && (
              <button type="button" className="relative" onClick={() => navigate("/platform/subscription-plans#owner-replies")} title={ownerReplies.unread ? `${ownerReplies.unread} owner repl${ownerReplies.unread === 1 ? "y" : "ies"} to read` : "Owner replies inbox"} data-testid="header-owner-replies">
                <Tray size={20} className={ownerReplies.unread ? "text-primary" : "text-muted-foreground"} weight={ownerReplies.unread ? "fill" : "regular"} />
                {ownerReplies.unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1" data-testid="owner-replies-badge">{ownerReplies.unread}</span>}
              </button>
            )}
            {/* Notifications dropdown */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <button className="relative" data-testid="header-notifications">
                  <Bell size={20} className="text-muted-foreground" />
                  {unreadCount > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold px-1" data-testid="notif-unread-badge">{unreadCount}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <h3 className="text-sm font-semibold font-[Chivo]">Notifications</h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-[10px] text-primary hover:underline font-medium" data-testid="notif-mark-all-read">
                        Mark all read
                      </button>
                    )}
                    <button onClick={toggleSound} className={`p-1 rounded transition-colors ${soundEnabled ? "text-primary hover:bg-primary/10" : "text-muted-foreground hover:bg-muted"}`} title={soundEnabled ? "Mute notification sound" : "Enable notification sound"} data-testid="notif-sound-toggle">
                      {soundEnabled ? <BellRinging size={14} /> : <Bell size={14} />}
                    </button>
                    <button onClick={toggleBrowserNotif} className={`p-1 rounded transition-colors ${browserNotifEnabled ? "text-primary hover:bg-primary/10" : "text-muted-foreground hover:bg-muted"}`} title={browserNotifEnabled ? "Disable browser notifications" : "Enable browser notifications"} data-testid="notif-browser-toggle">
                      <BellRinging size={14} />
                    </button>
                  </div>
                </div>
                <ScrollArea className="max-h-[400px]">
                  {notifLoading ? (
                    <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" /></div>
                  ) : notifications.length === 0 ? (
                    <div className="text-center py-10 px-4">
                      <Bell size={24} className="mx-auto text-muted-foreground mb-2" />
                      <p className="text-xs text-muted-foreground">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {notifications.slice(0, 20).map(n => {
                        const Icon = Bell;
                        return (
                          <button
                            key={n.id}
                            onClick={() => handleNotifClick(n)}
                            className={`w-full text-left flex items-start gap-2.5 px-4 py-3 transition-colors hover:bg-muted/50 ${!n.read ? "bg-primary/5" : ""}`}
                            data-testid={`notif-item-${n.id}`}
                          >
                            <Icon size={16} className={`mt-0.5 shrink-0 ${!n.read ? "text-primary" : "text-muted-foreground"}`} weight="duotone" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className={`text-xs font-medium truncate ${n.read ? "text-muted-foreground" : ""}`}>{n.title}</span>
                                {!n.read && <span className="shrink-0 h-1.5 w-1.5 rounded-full bg-primary" />}
                              </div>
                              <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                              <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
                <div className="border-t border-border px-4 py-2.5">
                  <button
                    onClick={() => { setNotifOpen(false); navigate("/notifications"); }}
                    className="w-full text-center text-xs text-primary hover:underline font-medium"
                    data-testid="notif-view-all"
                  >
                    View all notifications
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">{user?.name?.charAt(0)?.toUpperCase()}</div>
              {user && <div className="text-right hidden sm:block"><p className="text-xs font-medium leading-none">{user.name}</p><p className="text-[10px] text-muted-foreground">Platform Admin</p></div>}
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn"><SignOut size={18} /></Button>
          </div>
        </header>

        <main className="flex-1 overflow-auto"><div className="p-4 sm:p-6 lg:p-8">{children}</div></main>
      </div>
    </div>
  );
}
